import { SCHOOL_YEAR_OPTIONS } from "./age.js";

// Difficulty Ladder (docs/DIFFICULTY_LADDER_SCOPE.md) — shared year-keyed rung
// resolution for Maths Workout bands and Spelling lists. Vocab and TT Streaks are
// deliberately NOT on the ladder (Alan, 26 Aug: "no change to vocab and TT").
//
// The whole design in one sentence: bands/lists may carry a `years` array of school
// years ("Reception", "Y1"…"Y10"), and Auto resolution prefers the rung claiming the
// child's resolveSchoolYear() — parent-confirmed year first, live estimate otherwise —
// which is what actually separates a young 6 (Y1) from an old 7 (Y3), something no
// age-range banding can do.
//
// CRITICAL rollout property: no hardcoded band or list carries `years` — year metadata
// arrives ONLY via the CMS import (cms-seed/difficulty-ladder.json). Until Alan
// imports it, resolveRungByYear finds no year-keyed rungs, returns null, and every
// caller falls through to its existing age logic — i.e. deploying this code changes
// NOTHING for any child. The import is the approval act.

// Keep only real school-year strings — CMS values are operator-typed JSON.
export function sanitizeYears(v) {
  if (!Array.isArray(v)) return [];
  return v.filter((y) => SCHOOL_YEAR_OPTIONS.includes(y));
}

function yearRank(year) {
  const i = SCHOOL_YEAR_OPTIONS.indexOf(year);
  return i === -1 ? null : i;
}

// Picks a rung id for a school year from { id: { years, sortOrder... } } + an ordered
// id list. Returns null when year-resolution can't answer, meaning "use the age
// fallback" — three distinct cases, each deliberate:
//   - no rung anywhere carries years (ladder content not imported yet)
//   - the child has no resolvable school year
//   - the year falls in a GAP between year-keyed rungs (e.g. Y2 before Climber's
//     content is approved) — NOT nearest-match, because until a year's own content
//     exists the right behaviour is exactly today's, not a guess at an adjacent rung.
// Years OUTSIDE the ladder's range clamp to the nearest end (a Pre-schooler plays the
// Reception rung, a Y9 plays the Y7+ rung) — same clamping philosophy as the existing
// age resolvers.
export function resolveRungByYear(entriesById, order, schoolYear) {
  const yeared = order.filter((id) => entriesById[id] && sanitizeYears(entriesById[id].years).length);
  if (!yeared.length) return null;
  const rank = yearRank(schoolYear);
  if (rank === null) return null;

  let lowestId = null, lowestRank = Infinity, highestId = null, highestRank = -Infinity;
  for (const id of yeared) {
    const ranks = sanitizeYears(entriesById[id].years).map(yearRank).filter((r) => r !== null);
    if (ranks.includes(rank)) return id; // exact claim wins immediately
    const lo = Math.min(...ranks), hi = Math.max(...ranks);
    if (lo < lowestRank) { lowestRank = lo; lowestId = id; }
    if (hi > highestRank) { highestRank = hi; highestId = id; }
  }
  if (rank < lowestRank) return lowestId;
  if (rank > highestRank) return highestId;
  return null; // in-range gap → age fallback
}

// Parent-facing year label for a rung: "Reception", "Year 3", or "Year 7+" for a run
// of years reaching the top of SCHOOL_YEAR_OPTIONS. Multi-year rungs that don't reach
// the top render as "Years 3–4". Kid surfaces never show this — fun name only.
export function yearBadge(years) {
  const ys = sanitizeYears(years);
  if (!ys.length) return "";
  const label = (y) => (y === "Pre-school" || y === "Reception" ? y : `Year ${y.slice(1)}`);
  if (ys.length === 1) return label(ys[0]);
  const ranks = ys.map(yearRank).sort((a, b) => a - b);
  const first = SCHOOL_YEAR_OPTIONS[ranks[0]];
  const last = SCHOOL_YEAR_OPTIONS[ranks[ranks.length - 1]];
  if (last === SCHOOL_YEAR_OPTIONS[SCHOOL_YEAR_OPTIONS.length - 1]) return `${label(first)}+`;
  return `${label(first).replace("Year", "Years")}–${last.slice(1)}`;
}
